import { Injectable } from '@angular/core';
import { from, Observable, of, EMPTY, throwError, forkJoin, defer } from 'rxjs';
import {
  mergeMap,
  catchError,
  finalize,
  tap,
  map,
  switchMap,
  takeWhile,
  toArray
} from 'rxjs/operators';
import {
  TileProcessingItem,
  TileProcessingStatus,
  ProcessingState
} from '../../shared/models/csv-tile';
import { TilesService } from '../../../../core/services/tiles.service';
import { Tile, TileId, TILE_TYPE_SIMPLE_ANALOG, TILE_TYPE_SIMPLE_BOOL } from '../../../../shared/models/tile';
import { TileSetPointConversion, ConversionFormula } from '../../../../shared/models/set-point';
import { SimpleAnalogStructure, SimpleBoolStructure, ConditionMode } from '../../../../shared/models/structure';
import { findAnalogCondition } from '../../../../shared/utils/condition.util';
import { CompareOperator } from '../../../../shared/constants/tile-config.constants';
import { CSVFieldParser } from '../../shared/utils/csv-field-parser.util';
import { HttpErrorResponse } from '@angular/common/http';
import { BaseBatchProcessorService } from '../../shared/services/base-batch-processor.service';
import { AppId } from '../../../../shared/models/app';
import { CsvTileImporterService } from '../../shared/services/csv-tile-importer.service';
import { AppsService } from '../../../../core/services/apps.service';
import { CsvFieldId } from '../../shared/constants/csv-columns.constants';
import { isValidTileColor } from '@shared/utils/color.util';
import { TagService } from '../../../../core/services/tag.service';
import { Tag, TagAssignment, TagType } from '../../../../shared/models/tag';
import { ObjectType } from '../../../../shared/models/object.model';
import { TAG_PROFILES } from '../../../../shared/constants/tag-profiles.constants';

/**
 * Service for batch updating tile configurations from CSV
 * Extends BaseBatchProcessorService for common batch processing infrastructure
 * Handles tile configuration updates, app linking/unlinking, and revert functionality
 */
@Injectable()
export class BulkTileConfigurationService extends BaseBatchProcessorService {

  private labelToTagId = new Map<string, string>(); // label.toUpperCase() -> TagID
  private allUserTags: Tag[] = [];

  constructor(
    private tilesService: TilesService,
    private csvMapperService: CsvTileImporterService,
    private appsService: AppsService,
    private tagService: TagService
  ) {
    super();
  }

  startProcessing(items: TileProcessingItem[]): void {
    if (this.processingState() === ProcessingState.PROCESSING ||
        this.processingState() === ProcessingState.PAUSED) {
      return;
    }

    this.labelToTagId.clear();
    this.allUserTags = [];

    this.initializeProcessing(items);
    this.restartProcessing(items);
  }

  protected restartProcessing(items: TileProcessingItem[]): void {
    this.preResolveTagsAndFacets(items).pipe(
      switchMap(() => from(items).pipe(
        takeWhile(() => this.processingState() === ProcessingState.PROCESSING),
        mergeMap(item => this.updateTileConfiguration(item), this.config.batchSize),
        toArray(),
      )),
      switchMap(() => this.addTilesToFolders(items)),
      finalize(() => {
        if (this.processingState() === ProcessingState.PROCESSING) {
          this.stopProcessing();
        }
      })
    ).subscribe();
  }

  private preResolveTagsAndFacets(items: TileProcessingItem[]): Observable<void> {
    const uniqueLabels = new Set<string>();

    for (const item of items) {
      this.csvMapperService.parseTagLabels((item.csvData as any)[CsvFieldId.TAGS])
        .forEach(l => uniqueLabels.add(l));
    }

    if (uniqueLabels.size === 0) {
      return of(undefined);
    }

    return this.tagService.getUserTags$().pipe(
      tap(existingTags => { this.allUserTags = existingTags; }),
      switchMap(existingTags => {
        const labelsToCreate: string[] = [];
        for (const label of uniqueLabels) {
          const found = existingTags.find(t => t.Label.toUpperCase() === label.toUpperCase());
          if (found) {
            this.labelToTagId.set(label.toUpperCase(), found.ID);
          } else {
            labelsToCreate.push(label);
          }
        }

        if (labelsToCreate.length === 0) {
          return of(undefined);
        }

        return forkJoin(labelsToCreate.map(label =>
          this.tagService.createOrFindTag$(label).pipe(
            tap(tag => this.labelToTagId.set(label.toUpperCase(), tag.ID)),
            catchError(() => of(null))
          )
        )).pipe(map(() => undefined));
      }),
      map(() => undefined),
      catchError(() => of(undefined))
    );
  }

  /**
   * Revert tiles back to their original state before CSV updates
   * Only reverts tiles that were successfully updated
   * Returns an observable that completes when all reverts are done
   */
  revertUpdates(): Observable<{ success: number; failed: number }> {
    const items = this.processingItems();

    // Filter items that have originalTile and were successfully updated
    const revertableItems = items.filter(item =>
      item.originalTile &&
      item.status === TileProcessingStatus.UPDATED
    );

    if (revertableItems.length === 0) {
      return of({ success: 0, failed: 0 });
    }

    let successCount = 0;
    let failedCount = 0;

    // Create observables for all revert operations
    const revertObservables = revertableItems.map(item => {
      // Mark as processing
      this.updateStatus(item.id, TileProcessingStatus.PROCESSING);

      // Save the original tile back
      return this.tilesService.saveTile$(item.originalTile).pipe(
        tap(() => {
          // Restore the tile reference and reset status
          item.tile = item.originalTile;
          item.error = undefined; // Clear any error from previous processing
          successCount++;
          this.updateStatus(item.id, TileProcessingStatus.QUEUED);
        }),
        catchError(error => {
          // Handle revert error
          failedCount++;
          this.updateStatus(item.id, TileProcessingStatus.ERROR_GENERAL, `Failed to revert: ${error?.message || 'Unknown error'}`);
          return of(null); // Continue with other reverts
        })
      );
    });

    // Execute all reverts in parallel
    return from(revertObservables).pipe(
      mergeMap(obs => obs, 5), // Process 5 at a time
      toArray(), // Wait for all reverts to complete before emitting
      map(() => ({ success: successCount, failed: failedCount }))
    );
  }

  private updateTileConfiguration(item: TileProcessingItem): Observable<void> {
    // Check if we should continue processing before updating status
    if (this.processingState() !== ProcessingState.PROCESSING) {
      return EMPTY;
    }

    // Update item status to processing
    this.updateStatus(item.id, TileProcessingStatus.PROCESSING);

    // Initialize linking tracking
    item.linkedApps = [];
    item.unlinkedApps = [];
    item.linkedSuperTiles = [];
    item.failedLinks = { apps: [], superTiles: [] };

    // Get the original tile first (before updates)
    const existingTile = this.tilesService.sessUserTiles[item.tileId];
    const originalTile$ = existingTile
      ? from([existingTile])
      : this.tilesService.fetchTile$(item.tileId);

    // Store original, update tile, then update app links
    return originalTile$.pipe(
      takeWhile(() => this.processingState() === ProcessingState.PROCESSING),
      tap(originalTile => {
        // Store the original tile for potential revert
        if (!item.originalTile) {
          item.originalTile = Tile.create(originalTile);
        }
      }),
      mergeMap(originalTile =>
        this.updateSingleTileConfiguration(item.tileId, item.csvData).pipe(
          takeWhile(() => this.processingState() === ProcessingState.PROCESSING),
          map(updatedTile => ({ updatedTile, originalTile }))
        )
      ),
      switchMap(({ updatedTile, originalTile }) => {
        item.tile = updatedTile;
        return this.updateItemAppLinks(item, originalTile).pipe(
          map(() => ({ updatedTile, originalTile }))
        );
      }),
      switchMap(({ originalTile }) =>
        this.updateItemTagLinks(item, originalTile)
      ),
      tap({
        next: () => {
          if (this.requestedFolderIds(item).length > 0) {
            this.updateStatus(item.id, TileProcessingStatus.AWAITING_FOLDER_LINKING, undefined, item.tileId);
          } else {
            this.updateItemStatusAfterUpdate(item);
          }
        },
        error: (error) => {
          this.handleUpdateError(item, error);
        }
      }),
      map(() => undefined),
      catchError(() => of(undefined))
    );
  }

  /**
   * Updates app links for a tile based on CSV data
   * Calculates diff between current and requested apps, then links/unlinks accordingly
   */
  private updateItemAppLinks(item: TileProcessingItem, originalTile: Tile): Observable<void> {
    if (!item.tileId) {
      return of(undefined);
    }

    // Parse requested app IDs from CSV
    const requestedAppIds = this.csvMapperService.parseLinkedAppIds(item.csvData[CsvFieldId.LINKED_APP_IDS]);

    // Fetch current apps linked to the tile
    return this.appsService.getAppsLinkedToTile$(item.tileId).pipe(
      switchMap(apps => {
        // Get current app IDs
        const currentAppIds = apps.map(app => app.ID);

        // Calculate diff
        const appsToLink = requestedAppIds.filter(appId => !currentAppIds.includes(appId));
        const appsToUnlink = currentAppIds.filter(appId => !requestedAppIds.includes(appId));

        // If no changes needed, return immediately
        if (appsToLink.length === 0 && appsToUnlink.length === 0) {
          return of(undefined);
        }

        // Update status to LINKING_APPS
        this.updateStatus(item.id, TileProcessingStatus.LINKING_APPS, undefined, item.tileId);

        // Perform link/unlink operations
        return this.tilesService.updateAppLinks$(item.tileId, appsToLink, appsToUnlink).pipe(
          tap(() => {
            // Track successfully linked and unlinked apps separately
            item.linkedApps = appsToLink;      // Apps that were newly linked
            item.unlinkedApps = appsToUnlink;  // Apps that were unlinked
          }),
          catchError((error) => {
            // On error, track which apps we tried to link vs unlink separately
            if (!item.failedLinks!.appsToLink) {
              item.failedLinks!.appsToLink = [];
            }
            if (!item.failedLinks!.appsToUnlink) {
              item.failedLinks!.appsToUnlink = [];
            }
            appsToLink.forEach(appId => item.failedLinks!.appsToLink!.push(appId));
            appsToUnlink.forEach(appId => item.failedLinks!.appsToUnlink!.push(appId));
            return of(undefined);
          }),
          map(() => undefined)
        );
      }),
      catchError((error) => {
        // If we can't fetch current apps, we can't determine what to link/unlink
        // Mark all requested app IDs as failed to link (since we couldn't process them)
        const requestedAppIds = this.csvMapperService.parseLinkedAppIds(item.csvData[CsvFieldId.LINKED_APP_IDS]);
        if (!item.failedLinks!.appsToLink) {
          item.failedLinks!.appsToLink = [];
        }
        requestedAppIds.forEach(appId => item.failedLinks!.appsToLink!.push(appId));
        return of(undefined);
      })
    );
  }

  private requestedFolderIds(item: TileProcessingItem): TileId[] {
    return this.csvMapperService.parseLinkedSuperTileIds(item.csvData[CsvFieldId.LINKED_SUPER_TILE_IDS]);
  }

  private addTilesToFolders(items: TileProcessingItem[]): Observable<void> {
    if (this.processingState() !== ProcessingState.PROCESSING) {
      return of(undefined);
    }

    const awaitingItems = items.filter(item =>
      !!item.tileId && item.status === TileProcessingStatus.AWAITING_FOLDER_LINKING
    );
    const childrenByFolder = new Map<TileId, TileProcessingItem[]>();

    awaitingItems.forEach(item => {
      this.requestedFolderIds(item).forEach(folderId => {
        childrenByFolder.set(folderId, [...(childrenByFolder.get(folderId) ?? []), item]);
      });
    });

    if (childrenByFolder.size === 0) {
      return of(undefined);
    }

    awaitingItems.forEach(item =>
      this.updateStatus(item.id, TileProcessingStatus.LINKING_SUPER_TILES, undefined, item.tileId)
    );

    return from(childrenByFolder.entries()).pipe(
      mergeMap(([folderId, children]) => this.addChildrenToFolder(folderId, children), this.config.batchSize),
      toArray(),
      tap(() => awaitingItems.forEach(item => this.updateItemStatusAfterUpdate(item))),
      map(() => undefined)
    );
  }

  private addChildrenToFolder(folderId: TileId, children: TileProcessingItem[]): Observable<void> {
    return this.tilesService.addChildrenToFolder$(folderId, children.map(child => child.tileId!)).pipe(
      tap(() => children.forEach(child => child.linkedSuperTiles!.push(folderId))),
      catchError(() => {
        children.forEach(child => child.failedLinks!.superTiles!.push(folderId));
        return of(undefined);
      }),
      map(() => undefined)
    );
  }

  private updateItemTagLinks(item: TileProcessingItem, originalTile: Tile): Observable<void> {
    const tagsColumnPresent = (item.csvData as any)[CsvFieldId.TAGS] !== undefined;
    const facetsColumnPresent = (item.csvData as any)[CsvFieldId.FACETS] !== undefined;

    if (!tagsColumnPresent && !facetsColumnPresent) {
      return of(undefined);
    }

    this.updateStatus(item.id, TileProcessingStatus.LINKING_TAGS, undefined, item.tileId);

    const desiredTags$: Observable<Tag[]> = tagsColumnPresent
      ? this.resolveDesiredTags((item.csvData as any)[CsvFieldId.TAGS])
      : of(originalTile.tags);

    const profileKey = this.csvMapperService.resolveProfileKeyFromCsv(item.csvData);

    const desiredFacets$: Observable<Tag[]> = facetsColumnPresent
      ? this.resolveDesiredFacets((item.csvData as any)[CsvFieldId.FACETS], profileKey)
      : of(originalTile.facets as Tag[]);

    return forkJoin([desiredTags$, desiredFacets$]).pipe(
      switchMap(([desiredTags, desiredFacets]) => {
        const currentAll: Tag[] = [...originalTile.tags, ...(originalTile.facets as Tag[])];
        const desiredAll: Tag[] = [...desiredTags, ...desiredFacets];

        const tagKey = (t: Tag) => `${t.ID}:${t.Value ?? ''}`;
        const desiredKeys = new Set(desiredAll.map(tagKey));
        const currentKeys = new Set(currentAll.map(tagKey));

        const toRemove = currentAll.filter(t => !desiredKeys.has(tagKey(t)));
        const toAdd = desiredAll.filter(t => !currentKeys.has(tagKey(t)));

        if (toRemove.length === 0 && toAdd.length === 0) {
          return of(undefined as void);
        }

        const removeOps: Observable<void>[] = toRemove.map(t =>
          this.tagService.removeTag$(ObjectType.TILE, item.tileId!, t.ID)
        );

        const addOp: Observable<void> = toAdd.length > 0
          ? this.tagService.assignTags$(ObjectType.TILE, item.tileId!, toAdd.map(t => ({ TagID: t.ID, Value: t.Value } as TagAssignment)))
          : of(void 0);

        return forkJoin([...removeOps, addOp]).pipe(
          tap(() => {
            const finalTags = [
              ...currentAll.filter(t => desiredKeys.has(tagKey(t))),
              ...toAdd
            ];
            this.tilesService.updateTileTags(item.tileId!, finalTags);
          }),
          map(() => undefined as void)
        );
      }),
      catchError(error => {
        item.tagLinkingFailed = true;
        item.error = `ERROR_TAG_LINKING: ${error?.message ?? 'Tag linking failed'}`;
        return of(undefined);
      })
    );
  }

  private resolveDesiredTags(csvValue: string): Observable<Tag[]> {
    const labels = this.csvMapperService.parseTagLabels(csvValue);
    if (labels.length === 0) return of([]);

    return forkJoin(labels.map(label => {
      const cachedId = this.labelToTagId.get(label.toUpperCase());
      const cachedTag = cachedId ? this.allUserTags.find(t => t.ID === cachedId) : null;
      if (cachedTag) return of(cachedTag);

      return this.tagService.createOrFindTag$(label).pipe(
        tap(tag => this.labelToTagId.set(label.toUpperCase(), tag.ID))
      );
    }));
  }

  private resolveDesiredFacets(csvValue: string, profileKey?: string | null): Observable<Tag[]> {
    const pairs = this.csvMapperService.parseFacetPairs(csvValue);
    if (pairs.length === 0) return of([]);

    return defer(() => {
      const facets = profileKey
        ? TAG_PROFILES[profileKey].Facets
        : Object.values(TAG_PROFILES).flatMap(p => p.Facets);

      return of(pairs.map(({ key, value }) => {
        const f = facets.find(pf => pf.Label.toUpperCase() === key.toUpperCase());
        if (!f) throw new Error(`Facet "${key}" not found in profile`);
        return { ID: f.ID, Label: f.Label, Type: TagType.FACET, Value: value.toUpperCase() } as Tag;
      }));
    });
  }

  /**
   * Determines final status after tile update, app linking and tag linking
   */
  private updateItemStatusAfterUpdate(item: TileProcessingItem): void {
    const failedToLink = item.failedLinks?.appsToLink?.length || 0;
    const failedToUnlink = item.failedLinks?.appsToUnlink?.length || 0;
    const failedFolders = item.failedLinks?.superTiles?.length || 0;
    const totalFailed = failedToLink + failedToUnlink + failedFolders;

    if (totalFailed === 0) {
      if (item.tagLinkingFailed) {
        this.updateStatus(item.id, TileProcessingStatus.ERROR_TAG_LINKING, item.error, item.tileId);
      } else {
        this.updateStatus(item.id, TileProcessingStatus.UPDATED, undefined, item.tileId);
      }
      return;
    }

    // Some app linking operations failed - build detailed error message
    const errors: string[] = [];
    if (failedToLink > 0) {
      errors.push(`Failed to link: ${item.failedLinks!.appsToLink!.join(', ')}`);
    }
    if (failedToUnlink > 0) {
      errors.push(`Failed to unlink: ${item.failedLinks!.appsToUnlink!.join(', ')}`);
    }

    if (failedFolders > 0) {
      errors.push(`Failed to add to folders: ${item.failedLinks!.superTiles!.join(', ')}`);
    }

    const errorMsg = errors.join('; ');
    this.updateStatus(item.id, TileProcessingStatus.WARNING_PARTIAL, errorMsg, item.tileId);
  }

  private handleUpdateError(item: TileProcessingItem, error: any): void {
    let status = TileProcessingStatus.ERROR_GENERAL;
    let errorMessage = 'Failed to update tile configuration';

    if (error instanceof HttpErrorResponse) {
      if (error.status === 403 || error.status === 401) {
        status = TileProcessingStatus.ERROR_PERMISSIONS;
        errorMessage = 'You do not have permission to configure this tile';
      } else {
        errorMessage = error.error?.message || error.message || errorMessage;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }

    this.updateStatus(item.id, status, errorMessage);
  }

  // ===== Single Tile Configuration Methods =====

  /**
   * Update a single tile with configuration data from CSV
   */
  private updateSingleTileConfiguration(tileId: TileId, csvData: any): Observable<Tile> {
    // Try to get tile from session first
    const existingTile = this.tilesService.sessUserTiles[tileId];

    // If tile is in session, use it; otherwise fetch it from API
    const tile$ = existingTile
      ? of(existingTile)
      : this.tilesService.fetchTile$(tileId);

    return tile$.pipe(
      switchMap(tile => {
        // Build update object from CSV data
        const updates = this.buildTileUpdatesFromCsv(tile, csvData);

        // Create a new tile object with updates applied
        // Use Tile.create() to ensure we have a proper Tile instance with all methods
        const mergedData = Object.assign({}, tile, updates);
        const updatedTile = Tile.create(mergedData);

        // Call tiles service to save the tile
        return this.tilesService.saveTile$(updatedTile).pipe(
          map(() => updatedTile) // Return the updated tile
        );
      }),
      catchError(error => {
        console.error(`Failed to update tile ${tileId}:`, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Build tile update object from CSV data
   */
  private buildTileUpdatesFromCsv(tile: Tile, csvData: any): Partial<Tile> {
    const updates: any = {};

    // Common tile properties
    if (csvData[CsvFieldId.TILE_NAME]) {
      updates.Name = csvData[CsvFieldId.TILE_NAME];
    }

    // Appearance updates - deep copy to avoid mutating frozen objects
    if (this.hasCsvValue(csvData, CsvFieldId.DISPLAY_NAME) ||
        this.hasCsvValue(csvData, CsvFieldId.COLOR) ||
        this.hasCsvValue(csvData, CsvFieldId.ICON)) {
      updates.Appearance = {
        ...tile.Appearance,
        Config: { ...(tile.Appearance?.Config || {}) }
      };

      if (this.hasCsvValue(csvData, CsvFieldId.DISPLAY_NAME)) {
        updates.Appearance.DisplayName = csvData[CsvFieldId.DISPLAY_NAME];
      }
      if (this.hasCsvValue(csvData, CsvFieldId.COLOR)) {
        // Validate color - invalid colors are treated as empty string
        updates.Appearance.Color = isValidTileColor(csvData[CsvFieldId.COLOR]);
      }
      if (this.hasCsvValue(csvData, CsvFieldId.ICON)) {
        updates.Appearance.Icon = csvData[CsvFieldId.ICON];
      }
    }

    // Structure updates - deep copy nested objects including Conditions
    const structureWithConditions = tile.Structure as any;
    updates.Structure = {
      ...tile.Structure,
      Conditions: structureWithConditions.Conditions
        ? structureWithConditions.Conditions.map((cond: any) => ({
            ...cond,
            Comparison: cond.Comparison ? { ...cond.Comparison } : cond.Comparison
          }))
        : undefined
    };

    if (this.hasCsvValue(csvData, CsvFieldId.TILE_WRITABILITY)) {
      updates.Structure.Writable = this.parseBooleanValue(csvData[CsvFieldId.TILE_WRITABILITY]);
    }

    if (this.hasCsvValue(csvData, CsvFieldId.AUTO_CLEAR_ALARMS)) {
      updates.Structure.AutoClear = CSVFieldParser.parseAutoClear(csvData[CsvFieldId.AUTO_CLEAR_ALARMS]);
    }

    // Type-specific updates
    if (tile.Type === TILE_TYPE_SIMPLE_ANALOG) {
      this.applyNumericTileUpdates(updates, csvData, tile);
    } else if (tile.Type === TILE_TYPE_SIMPLE_BOOL) {
      this.applyToggleTileUpdates(updates, csvData, tile);
    }

    return updates;
  }

  /**
   * Apply numeric tile specific updates
   */
  private applyNumericTileUpdates(updates: any, csvData: any, tile: Tile): void {
    const structure = updates.Structure as SimpleAnalogStructure;

    // Ensure Appearance is a deep copy if not already set
    if (!updates.Appearance) {
      updates.Appearance = {
        ...tile.Appearance,
        Config: { ...(tile.Appearance?.Config || {}) }
      };
    }
    const appearance = updates.Appearance;

    // Data display
    if (this.hasCsvValue(csvData, CsvFieldId.DATA_DISPLAY_UNITS)) {
      structure.DisplayRawUnits = csvData[CsvFieldId.DATA_DISPLAY_UNITS];
    }

    // Ensure Config exists (it should from deep copy above, but be safe)
    if (!appearance.Config) {
      appearance.Config = {};
    }

    if (this.hasCsvValue(csvData, CsvFieldId.DECIMAL_PLACES)) {
      appearance.Config.Decimal = parseInt(csvData[CsvFieldId.DECIMAL_PLACES], 10);
    }

    if (this.hasCsvValue(csvData, CsvFieldId.TRANSFORMATION_CONVERSION_FORMULA)) {
      const conversionRaw = csvData[CsvFieldId.TRANSFORMATION_CONVERSION_FORMULA];
      const conversionNormalized = conversionRaw.toLowerCase() === 'none' ? ConversionFormula.None : conversionRaw;
      appearance.Config.Conversion = conversionNormalized as TileSetPointConversion;
    }

    if (this.hasCsvValue(csvData, CsvFieldId.MULTIPLIER)) {
      appearance.Config.Multiplier = parseFloat(csvData[CsvFieldId.MULTIPLIER]);
    }

    if (this.hasCsvValue(csvData, CsvFieldId.CALIBRATION_OFFSET)) {
      appearance.Config.Offset = parseFloat(csvData[CsvFieldId.CALIBRATION_OFFSET]);
    }

    // Writable range
    if (this.hasCsvValue(csvData, CsvFieldId.MINIMUM_WRITABLE_VALUE)) {
      appearance.Config.MinAllowed = parseFloat(csvData[CsvFieldId.MINIMUM_WRITABLE_VALUE]);
    }

    if (this.hasCsvValue(csvData, CsvFieldId.MAXIMUM_WRITABLE_VALUE)) {
      appearance.Config.MaxAllowed = parseFloat(csvData[CsvFieldId.MAXIMUM_WRITABLE_VALUE]);
    }

    updates.Appearance = appearance;

    // Alarm conditions — find each condition by Mode + operator direction rather than
    // by array index, because the tile editor reorders the array on save.
    if (!structure.Conditions) {
      structure.Conditions = [];
    }

    const getCondition = (mode: ConditionMode, dir: 'high' | 'low') => {
      let condition = findAnalogCondition(structure.Conditions, mode, dir);
      if (!condition) {
        condition = this.createDefaultCondition();
        structure.Conditions.push(condition);
      }
      return condition;
    };

    this.applyConditionUpdates(getCondition(ConditionMode.Critical, 'low'), csvData, 'Low Critical');
    this.applyConditionUpdates(getCondition(ConditionMode.Warning, 'low'), csvData, 'Low Warning');
    this.applyConditionUpdates(getCondition(ConditionMode.Warning, 'high'), csvData, 'High Warning');
    this.applyConditionUpdates(getCondition(ConditionMode.Critical, 'high'), csvData, 'High Critical');
  }

  /**
   * Apply toggle tile specific updates
   */
  private applyToggleTileUpdates(updates: any, csvData: any, tile: Tile): void {
    const structure = updates.Structure as SimpleBoolStructure;

    // Data display phrases
    if (this.hasCsvValue(csvData, 'Data Display True Phrase')) {
      structure.DisplayTrue = csvData['Data Display True Phrase'];
    }

    if (this.hasCsvValue(csvData, 'Data Display False Phrase')) {
      structure.DisplayFalse = csvData['Data Display False Phrase'];
    }

    // Critical alarm (single condition for toggle tiles)
    if (!structure.Conditions) {
      structure.Conditions = [];
    }

    if (structure.Conditions.length === 0) {
      structure.Conditions.push(this.createDefaultCondition());
    }

    this.applyConditionUpdates(structure.Conditions[0], csvData, 'Critical');
  }

  /**
   * Apply alarm condition updates from CSV
   * Note: Field names are constructed dynamically based on prefix (e.g., "Low Critical", "High Warning")
   * These match the CsvFieldId enum values
   */
  private applyConditionUpdates(condition: any, csvData: any, prefix: string): void {
    // Field names are constructed as: "${prefix}: Enabled", "${prefix}: Operator", etc.
    // These correspond to enum values like CsvFieldId.LOW_CRITICAL_ENABLED
    const enabledField = `${prefix}: Enabled`;
    const operatorField = `${prefix}: Operator`;
    const valueField = `${prefix}: Value`;
    const delayField = `${prefix}: Delay`;
    const messageField = `${prefix}: Message`;
    const iconField = `${prefix}: Icon`;

    if (this.hasCsvValue(csvData, enabledField)) {
      condition.Enabled = this.parseBooleanValue(csvData[enabledField]);
    }

    if (this.hasCsvValue(csvData, operatorField)) {
      condition.Comparison = condition.Comparison || {};
      condition.Comparison.Operator = csvData[operatorField] || CompareOperator.GreaterThan;
    }

    if (this.hasCsvValue(csvData, valueField)) {
      condition.Comparison = condition.Comparison || {};
      condition.Comparison.Value = parseFloat(csvData[valueField]);
    }

    if (this.hasCsvValue(csvData, delayField)) {
      condition.Delay = parseInt(csvData[delayField], 10);
    }

    if (this.hasCsvValue(csvData, messageField)) {
      condition.DisplayText = csvData[messageField];
    }

    if (this.hasCsvValue(csvData, iconField)) {
      condition.Icon = csvData[iconField];
    }
  }

  /**
   * Create a default condition object
   */
  private createDefaultCondition(): any {
    return {
      Enabled: false,
      Comparison: {
        Operator: CompareOperator.GreaterThan,
        Value: 0
      },
      Delay: 0,
      DisplayText: '',
      Icon: ''
    };
  }

  /**
   * Check if CSV has a non-empty value for a field
   */
  private hasCsvValue(csvData: any, field: string): boolean {
    return csvData[field] !== undefined && csvData[field] !== null && csvData[field] !== '';
  }

  /**
   * Parse boolean value from CSV string
   */
  private parseBooleanValue(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    const str = String(value).toLowerCase().trim();
    return str === 'true' || str === '1' || str === 'yes';
  }

}
